import { Fragment, useCallback, useEffect, useRef, useState } from "react"; // M10-14613
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { usePagination } from "src/hooks/usePagination";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { usePoHandlers } from "../PoController";
import { useToast } from "src/hooks/useToast";
import { SortingState } from "@tanstack/react-table";
import { Transition } from "@headlessui/react";
import * as yup from "yup";
import { RootState } from "src/context/store";
import Button from "src/features/fields/components/Button";
import Table from "src/features/fields/components/Table";
import Badge from "src/features/fields/components/Badge";
import POFilter from "src/features/fields/container/POFilter";
import DeleteModal from "src/container/DeleteModal";
import { createColumnHelper } from "@tanstack/react-table";
import FilterIcon from "src/assets/filter.svg";
import moment from "moment";
import { getStockistBlockList, updateStockistBlockRevoke, getStockistBlockLogs } from "src/services/stockist-block-api" // M10-14613
import { queryDivisionDetails } from "src/services/division-api";
import { getAllCompanyDetails } from "src/services/company-api";
import { poActions } from "src/context/po-slice";
import { appendParams } from "src/utils/utils";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@radix-ui/react-tooltip"; // M10-12458
import { Check, Copy, Eye, X } from "lucide-react";  // M10-14613

const status = [{ name: "Blocked", value: "blocked" }, { name: "Revoked", value: "revoked" }]

const blockType = [{ name: "EInv_Ack Pending", value: 1 }, { name: "SAO Block", value: 2 }, { name: "Others", value: 3 }]

const StockistBlockTab = () => {
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false)
  const token: any = useSelector((state: RootState) => state.auth);
  const { stockistBlockTableData, poFiltersData, stockistBlockFilters } = useSelector((state: any) => state.po);
  const { pagination, onPaginationChange, pageIndex, pageSize, resetPagination } = usePagination();
  const [totalRowCount, setTotalRowCount] = useState(10);
  const [isFiltersShown, setIsFiltersShown] = useState<boolean>(false);
  const [sorting, setSorting] = useState<SortingState>();
  const [globalSearch, setGlobalSearch] = useState("");
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [SelectedStockist, setSelectedStockist] = useState<{ [key: string]: any } | null>(null);
  // M10-14613
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsData, setLogsData] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [logsHasMore, setLogsHasMore] = useState(true); 
  const logsLimit = 10; 
  const logsPageRef = useRef(0); 
  const logsLoadingRef = useRef(false); 
  const logsHasMoreRef = useRef(true); 
  const logsDomainKeyRef = useRef<number | null>(null);
  const logsCompanyCodeRef = useRef<number>(0);
  const logsScrollRef = useRef<HTMLDivElement>(null);
  // M10-14613
  const revokeInProgressRef = useRef(false); // blocks double-submit until revoke API + list refresh complete
  const dispatch = useDispatch();

  const ref = useOutsideClick(() => {
    setIsFiltersShown(false);
  });

  const [_, setSearchParams] = useSearchParams();

  const { toast } = useToast();

  const revokeSchema = yup.object().shape({
    reference_number: yup.string().required("Reference number is required").max(150, "Remarks must not exceed 150 characters"),
  });

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor("stockist_metadata.company.companyName", {
      header: () => "Company Name",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("stockist_metadata.divisions.divisionName", {
      header: () => "Division Name",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("stockist_metadata.stockists.firm_name", {
      header: () => "Stockist Name",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("stockist_metadata.stockists.medvol_code", {
      header: () => "Stockist Code",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("company_stockist_code", {
      header: () => "Company Stockist Code",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
     columnHelper.accessor("stockist_metadata.stockists.compStockistAliasCode", {
      header: () => "Company Stockist Alias Code",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("stockist_metadata.products.product_name", {
      header: () => "Product Name",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("stockistBlockHdr.remarks_desc", {
      header: () => "Block Type",
      enableSorting: true,
      cell: (info: any) => info.getValue() || "-",
    }),
    columnHelper.accessor("reason", {
      header: () => "Remarks",
      enableSorting: true,
      cell: (info: any) => { // M10-12458
        const value = info.getValue() || "-";
        const isLong = value.length > 22;
        const displayText = isLong ? `${value.slice(0, 22)}...` : value;

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="cursor-pointer whitespace-nowrap"
                >
                  {displayText}
                </span>
              </TooltipTrigger>

              {isLong && (
                <TooltipContent side="top" align="center">
                  {value}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        );
      },
      // M10-12458
    }),
    columnHelper.accessor("is_blocked", {
      header: () => "Status",
      enableSorting: true,
      cell: (info: any) => {
        const isBlocked = Boolean(info.getValue());
        return (
          <Badge variant={isBlocked ? "error" : "success"} className="capitalize">
            {isBlocked ? "Blocked" : "Revoked"}
          </Badge>
        );
      },
    }),
    columnHelper.accessor("created_date", {
      header: () => "Blocked Date",
      enableSorting: true,
      cell: (info: any) => {
        const { modified_date } = info.row.original; // M10-14612
        const dateToShow = modified_date; // M10-14612

        return dateToShow
          ? moment(dateToShow)
            .tz("Asia/Kolkata")
            .format("DD MMM YYYY")
          : "-";
      },
    }),
    columnHelper.accessor("actions", {
      header: () => <span className="ml-auto mr-2">Stockist Revoke</span>,
      enableSorting: false,
      size: 124,
      cell: ({ row }: any) =>
        <div className="flex justify-end w-full">
          <Button
            disabled={row?.original?.active_status === "N"}
            className={row?.original?.active_status === "N" ? `bg-gray-100 px-6 border` : `px-6 border`}
            onClick={() => {
              revokeFunctionHandler(row.original);
            }}
            module={fineGrainedPermissionsKeys.PURCHASEORDER.STOCKISTBLOCKREVOKEADD.module}
            action={fineGrainedPermissionsKeys.PURCHASEORDER.STOCKISTBLOCKREVOKEADD.action}
          >
            Revoke
          </Button>

        </div>
    }),
    // M10-14613
    columnHelper.accessor("Versioning", {
      header: () => <span className="ml-auto mr-2">Versioning</span>,
      enableSorting: false,
      size: 100,
      cell: ({ row }: any) =>
        <div className="ml-auto mr-2 w-full">
          <button
            onClick={() => handleViewLogs(row.original)}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
            title="View History"
          >
            <Eye size={16} />
          </button>
        </div>
    }),
  ];

  // M10-14613: infinite-scroll fetch, replaces the old fixed page=0/limit=10 one-shot call
  const fetchLogsPage = async (page: number, isFresh: boolean) => {
    if (logsLoadingRef.current || logsDomainKeyRef.current === null) return;
    logsLoadingRef.current = true;
    setLogsLoading(true);
    try {
      const res = await getStockistBlockLogs(logsDomainKeyRef.current, page, logsLimit, logsCompanyCodeRef.current);
      const newLogs = res?.data?.data?.logs || [];
      const more = newLogs.length === logsLimit;

      setLogsData((prev) => (isFresh ? newLogs : [...prev, ...newLogs]));
      logsHasMoreRef.current = more;
      setLogsHasMore(more);
      logsPageRef.current = page + 1;
    } catch (err) {
      toast({ description: "Failed to fetch logs.", variant: "destructive" });
    } finally {
      logsLoadingRef.current = false;
      setLogsLoading(false);
    }
  };

  const handleViewLogs = (rowData: any) => {
    logsDomainKeyRef.current = rowData.id;
    logsCompanyCodeRef.current = rowData.company_code || 0;
    logsPageRef.current = 0; // M10-14613
    logsHasMoreRef.current = true; // M10-14613
    setLogsHasMore(true); // M10-14613
    setLogsData([]);
    setShowLogsModal(true);
    fetchLogsPage(0, true); // M10-14613
  };

  // M10-14613: triggers next page fetch when scrolled near the bottom of the logs modal
  const handleLogsScroll = useCallback(() => {
    const el = logsScrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 150;
    if (isNearBottom && logsHasMoreRef.current && !logsLoadingRef.current) {
      fetchLogsPage(logsPageRef.current, false);
    }
  }, []);
  // M10-14613

  const revokeFunctionHandler = async (rowData: any) => {
    if (revokeInProgressRef.current) return;
    setShowRevokeModal(true);
    setSelectedStockist(rowData);
  };

  const handleStockistRevoke = async (remarksDetails: any) => {
    // Ref guard: state-based `isLoading` isn't applied until re-render, so a fast double click can slip through
    if (revokeInProgressRef.current) return;

    if (!SelectedStockist) {
      toast({ description: "No stockist selected." });
      return;
    }

    const {
      company_stockist_code,
      company_code,
      division_code,
      item_code,
      stockistBlockHdr,
    } = SelectedStockist;

    const created_by = token?.token?.sub;
    const reason = remarksDetails?.reference_number;

    if (!reason) {
      toast({ description: "Remarks is required." });
      return;
    }

    const payload = {
      data: {
        company_stockist_code,
        company_code,
        division_code,
        item_code,
        created_by,
        reason,
        reference_number: reason,
        is_blocked: false,
        remarks_id: stockistBlockHdr?.remarks_id,
        target: "V2",
      },
    };

    revokeInProgressRef.current = true;
    let keepLocked = false;

    try {
      setIsLoading(true);

      const res = await updateStockistBlockRevoke(payload, company_code);

      if (res?.status === 200) {
        if (res?.data?.status === false) {
          toast({ description: res?.data?.message || "Unable to revoke stockist." });
        } else {
          toast({ description: "Stockist revoked successfully." });
          setSelectedStockist(null);
          // Stay locked until the list reflects the revoke, so the same row can't be revoked again
          keepLocked = true;
          setTimeout(() => {
            fetchDetails().finally(() => {
              revokeInProgressRef.current = false;
            });
          }, 2000);
        }
      } else {
        console.error("Failed to revoke the stockist:", res?.data);
        toast({ description: res?.data?.message || "Failed to revoke stockist.", variant: "destructive" });
      }
    } catch (err: any) {
      console.error("Error revoking stockist:", err);
      toast({
        description:
          err?.response?.data?.message ||
          err?.message ||
          "An unexpected error occurred while revoking stockist.",
      });
    } finally {
      setShowRevokeModal(false);
      setIsLoading(false);
      if (!keepLocked) revokeInProgressRef.current = false;
    }
  };

  const fetchDetails = () => {
    setLoading(true);

    setSearchParams({
      pageSize: pageSize.toString(),
      pageIndex: pageIndex.toString(),
    });

    const companyParams = appendParams(Array.isArray(stockistBlockFilters?.company) ? stockistBlockFilters.company : [], "company", "code");
    const divisionParams = appendParams(
      Array.isArray(stockistBlockFilters?.division) ? stockistBlockFilters.division : [],
      "division",
      "company_divisioncode"
    );

    const blockTypeParams = appendParams(
      Array.isArray(stockistBlockFilters?.blockType) ? stockistBlockFilters.blockType : [],
      "blockType",
      "value"
    );


    let filtersQuery = [companyParams, divisionParams, blockTypeParams].join("&");

    filtersQuery.endsWith("&&") && (filtersQuery = filtersQuery.slice(0, -1));
    filtersQuery.endsWith("&") && (filtersQuery = filtersQuery.slice(0, -1));

    const selectedStatuses = stockistBlockFilters?.status || [];

    const hasBlocked = selectedStatuses.some((s: any) => s.value === "blocked");
    const hasRevoked = selectedStatuses.some((s: any) => s.value === "revoked");

    let is_blocked: boolean | undefined = undefined;

    if (hasBlocked && !hasRevoked) {
      is_blocked = true;
    } else if (!hasBlocked && hasRevoked) {
      is_blocked = false;
    } else {
      is_blocked = undefined;
    }

    return getStockistBlockList(
      is_blocked,
      pageIndex - 1,
      pageSize,
      filtersQuery,
      globalSearch ? globalSearch : undefined,
      stockistBlockFilters?.company?.[0]?.code === 618 ? stockistBlockFilters?.company?.[0]?.code : 0
    )
      .then((res) => {
        dispatch(poActions.setStockistBlockTableData(res?.data?.data?.data || []));
        setTotalRowCount(res?.data?.data?.totalDataCount);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDetails();
  }, [pageSize, pageIndex, sorting, globalSearch]);

  let pageCount = Math.ceil(totalRowCount / (pageSize ?? 1));
  if (totalRowCount % 10 === 0) {
    pageCount--;
  }

  let timerId: any;
  const handleSearch = (searchQuery: string) => {
    if (timerId) clearTimeout(timerId);

    timerId = setTimeout(() => {
      resetPagination();
      setGlobalSearch(searchQuery);
    }, 500);
  };

  const {
    fetchState,
  } = usePoHandlers();

  useEffect(() => {
    getAllCompanyDetails()
      .then((res) =>
        dispatch(poActions.setPoFiltersData({ key: "company", value: res.data }))
      )
      .catch((err) => console.log(err));

    if (stockistBlockFilters?.company?.length) {
      queryDivisionDetails(
        `/details/?fields=division_name&company_code=${stockistBlockFilters.company[0].code}`
      )
        .then((res) =>
          dispatch(poActions.setPoFiltersData({ key: "stockistBlockDivision", value: res.data }))
        )
        .catch((err) => console.log(err));
    } else {
      dispatch(poActions.setPoFiltersData({ key: "stockistBlockDivision", value: [] }));
      dispatch(poActions.setStockistBlockFilters({ key: "division", value: [] }));
    }

    fetchState?.();
  }, [stockistBlockFilters?.company, dispatch]);


  return (
    <>
      <Transition
        as={Fragment}
        show={isFiltersShown}
        enter="transition transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition  ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed right-0 inset-0 top-0 z-[500]">
          <POFilter
            ref={ref}
            companyDetails={poFiltersData?.company || []}
            divisionDetails={poFiltersData?.stockistBlockDivision || []}
            statusDetails={status}
            blockTypeDetails={blockType}
            source="StockistBlock"
            onCloseClick={() => setIsFiltersShown(false)}
          />
        </div>
      </Transition>

      <div className="flex justify-end float-right gap-2">
        <Button
          className="relative gap-1 px-4 mt-2 ml-2 border "
          onClick={() => {
            setIsFiltersShown(true);
          }}
        >
          Filters <img src={FilterIcon} className="w-5 h-5" />
          {stockistBlockFilters?.filterCount > 0 && (
            <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full">
              {stockistBlockFilters?.filterCount}
            </span>
          )}
        </Button>
        <Button onClick={fetchDetails} loaderSize="small" className="h-auto py-2 mt-2 border">
          Retrieve
        </Button>
      </div>

      <Table
        title="Stockist Block Requests"
        subtitle="View and manage all your stockist block-revoke requests."
        columns={columns}
        data={stockistBlockTableData ?? []}
        isLoading={loading}
        manualPagination={true}
        pageCount={pageCount}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        sorting={sorting}
        onSortingChange={setSorting}
        manualSorting={true}
        isGlobalSearch={true}
        onGlobalSearch={handleSearch}
      />

      <DeleteModal title="Stockist Revoke" label="Remarks" schema={revokeSchema} value={showRevokeModal} deleteText="Continue" setValue={setShowRevokeModal} loading={isLoading} onFormSubmit={handleStockistRevoke} />
      {/* M10-14613 */}
      {showLogsModal && (
        <div className="fixed inset-0 z-[600] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <h2 className="text-base font-semibold text-gray-800">Block / Revoke History</h2>
              <button onClick={() => setShowLogsModal(false)} className="p-1 rounded hover:bg-gray-100 text-gray-500">
                <X size={18} />
              </button>
            </div>
            <div className="overflow-auto flex-1" ref={logsScrollRef} onScroll={handleLogsScroll}> {/* M10-14613 */}
              {logsLoading && logsData.length === 0 ? (
                <div className="flex justify-center items-center py-12 text-sm text-gray-500">Loading...</div>
              ) : logsData.length === 0 ? (
                <div className="flex justify-center items-center py-12 text-sm text-gray-500">No logs found.</div>
              ) : (
                <>
                  <table className="w-full text-sm border-collapse">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        <th className="px-4 py-3 border-b whitespace-nowrap w-20">Version</th>
                        <th className="px-4 py-3 border-b whitespace-nowrap w-44">Date</th>
                        <th className="px-4 py-3 border-b whitespace-nowrap w-24">Action</th>
                        <th className="px-4 py-3 border-b whitespace-nowrap w-24">Status</th>
                        <th className="px-4 py-3 border-b whitespace-nowrap w-32">Block Type</th>
                        <th className="px-4 py-3 border-b whitespace-nowrap">Reason</th>
                        <th className="px-4 py-3 border-b whitespace-nowrap w-28">Updated By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logsData.map((log: any, idx: number) => {
                        const blockTypeLabel = blockType.find((b) => b.value === log.input?.remarks_id)?.name || "-";
                        const reason = log.input?.reference_number ? log.input?.reference_number : log.input?.reason || "-";
                        return (
                          <tr key={idx} className="group border-b last:border-0 hover:bg-gray-50">
                            <td className="px-4 py-3 text-center">{log.version ?? "-"}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs">
                              {log.created_at ? moment(log.created_at).tz("Asia/Kolkata").format("DD MMM YYYY, hh:mm A") : "-"}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${log.action === "CREATE" ? "bg-blue-100 text-blue-700" : "bg-yellow-100 text-yellow-700"}`}>
                                {log.action || "-"}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={log.input?.is_blocked ? "error" : "success"} className="capitalize">
                                {log.input?.is_blocked ? "Blocked" : "Revoked"}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">{blockTypeLabel}</td>
                            <td className="px-4 py-3 max-w-[200px]">
                              <div className="flex items-center gap-1.5">
                                <span className="line-clamp-3 group-hover:line-clamp-none break-words">{reason}</span>
                                {reason !== "-" && (
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(reason);
                                      setCopiedIdx(idx);
                                      setTimeout(() => setCopiedIdx(null), 1500);
                                    }}
                                    className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-700"
                                    title="Copy reason"
                                  >
                                    {copiedIdx === idx ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">{log.created_by?.name || "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {/* M10-14613: infinite-scroll loading/end indicators */}
                  {logsLoading && (
                    <div className="flex justify-center items-center py-4 text-sm text-gray-500">Loading more...</div>
                  )}
                  {!logsHasMore && (
                    <div className="flex justify-center items-center py-4 text-xs text-gray-400">No more records.</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* M10-14613 */}
    </>
  );
};

export default StockistBlockTab;
